<template>
  <div class="relative min-h-screen w-full flex bg-gradient-to-br from-gray-100 to-blue-50">
    <div class="h-full container mx-auto">
      <div class="flex flex-col h-full w-full p-0 sm:p-8">
        <nav class="text-sm text-gray-600 mb-6 flex">
          <router-link to="/dashboard" class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
          <span class="mx-1">/</span>
          <router-link to="/partnerships" class="hover:text-blue-700 transition font-medium">Partnerships</router-link>
          <span class="mx-1">/</span>
          <span class="text-blue-900 font-semibold">Document Control</span>
        </nav>

        <div class="header-section bg-white shadow-sm rounded-lg p-6 mb-6 border border-gray-100">
          <div class="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 class="text-2xl font-bold text-gray-900 mb-2">Document Control</h1>
              <p class="text-gray-600">Manage document records with reference logs and file attachments.</p>
            </div>

            <div class="flex items-center gap-2 flex-wrap">
              <button v-if="hasPermission('document-control-create')" type="button" @click.stop="createModal = true"
                class="dc-add-btn">
                <icon-plus class="dc-add-btn__icon"></icon-plus>
                <span>Add Document</span>
              </button>
            </div>
          </div>
        </div>

        <div class="bg-white shadow-sm rounded-lg flex-auto flex flex-col border border-gray-100">
          <div class="p-6 border-b border-gray-200">
            <div class="flex items-center justify-between gap-3 flex-wrap">
              <h2 class="text-lg font-semibold text-gray-900">Document records</h2>
              <button v-if="hasPermission('document-control-index')" type="button" @click.stop="generateReport"
                class="dc-export-btn" :disabled="isExportExcel">
                <font-awesome-icon :icon="['fas', 'file-excel']" class="dc-export-btn__icon" />
                <span>Export Excel</span>
              </button>
            </div>
          </div>

          <div v-if="isLoading" class="flex-auto flex justify-center items-center py-12">
            <div class="text-center max-w-md px-4">
              <div class="text-gray-700 font-medium mb-1">Loading…</div>
              <div class="text-gray-500 text-sm">Fetching document records.</div>
            </div>
          </div>

          <div v-else-if="documentControlRecords && documentControlRecords.length === 0"
            class="flex-auto flex justify-center items-center py-12">
            <div class="text-center max-w-md px-4">
              <h2 class="text-xl font-semibold text-gray-800 mb-2">No records yet</h2>
              <p class="text-gray-500 mb-4">Create a record to start tracking document movements and attachments.</p>
              <button v-if="hasPermission('document-control-create')" type="button" @click.stop="createModal = true"
                class="dc-add-btn">
                <icon-plus class="dc-add-btn__icon"></icon-plus>
                <span>Add Document</span>
              </button>
            </div>
          </div>

          <div v-else class="flex flex-col flex-auto">
            <div class="flex-auto flex flex-col overflow-x-auto">
              <div class="inline-block min-w-full py-2">
                <div class="overflow-x-auto">
                  <table class="min-w-full text-left text-sm font-light">
                    <thead class="border-b font-medium dark:border-neutral-500">
                      <tr>
                        <th scope="col" class="px-6 py-2 text-gray-400 w-8">#</th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Document</th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Reference Log</th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Document Type</th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Sent By</th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Sent To</th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Description</th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Files</th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Date</th>
                        <th scope="col" class="px-6 py-2 text-gray-400 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in documentControlRecords" :key="item.id"
                        class="border-b border-dashed dark:border-neutral-500 hover:bg-gray-50">
                        <td class="whitespace-nowrap px-6 py-2 font-medium text-gray-900">
                          {{ rowNumber(index) }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold" :class="{
                            'bg-green-50 text-green-800': item.document_direction === 'IN',
                            'bg-amber-50 text-amber-800': item.document_direction === 'OUT',
                            'bg-gray-100 text-gray-700': item.document_direction !== 'IN' && item.document_direction !== 'OUT'
                          }">
                            {{ item.document_direction ? item.document_direction : '—' }}
                          </span>
                        </td>
                        <td class="px-6 py-2 text-gray-900 whitespace-nowrap">
                          <div class="font-mono text-xs text-gray-900">
                            {{ item.reference_log }}
                          </div>
                        </td>
                        <td class="px-6 py-2 text-gray-900 whitespace-nowrap">
                          {{ item.document_type && item.document_type.name ? item.document_type.name : '—' }}
                        </td>
                        <td class="px-6 py-2 text-gray-900 whitespace-nowrap">
                          {{ item.created_by_user && item.created_by_user.name ? item.created_by_user.name : '—' }}
                        </td>
                        <td class="px-6 py-2 text-gray-900 whitespace-nowrap">
                          {{ item.sent_to_user && item.sent_to_user.name ? item.sent_to_user.name : '—' }}
                        </td>
                        <td class="px-6 py-2 text-gray-800">
                          <div class="max-w-xs truncate" :title="item.description ? item.description : ''">
                            {{ item.description ? item.description : '—' }}
                          </div>
                        </td>
                        <td class="px-6 py-2">
                          <button v-if="item.attachments && item.attachments.length > 0" type="button"
                            class="dc-file-icon-btn" title="View attachment"
                            @click.stop="openAttachmentPreview(item.attachments[0])">
                            <font-awesome-icon :icon="['fas', 'paperclip']" />
                          </button>
                          <button v-else-if="hasPermission('document-control-update')" type="button"
                            class="dc-file-upload-btn" title="Upload attachment"
                            @click.stop="openUploadAttachmentModal(item)">
                            <font-awesome-icon :icon="['fas', 'plus']" />
                          </button>
                          <span v-else class="text-gray-400">—</span>
                        </td>
                        <td class="px-6 py-2 text-gray-900 whitespace-nowrap">
                          {{ formatDateDDMMYYYY(item.created_at) }}
                        </td>
                        <td class="px-6 py-2">
                          <div class="flex justify-end">
                            <button v-if="hasPermission('document-control-update')" type="button" title="Edit"
                              class="bg-gray-100 hover:bg-indigo-50 w-8 h-8 flex justify-center items-center rounded-lg mr-2"
                              @click.stop="openEdit(item)">
                              <icon-pen class="w-4 h-4"></icon-pen>
                            </button>
                            <button v-if="hasPermission('document-control-delete')" type="button" title="Delete"
                              class="bg-gray-100 hover:bg-red-50 w-8 h-8 flex justify-center items-center rounded-lg"
                              @click.stop="deleteItem(item.id)">
                              <icon-trash class="w-4 h-4"></icon-trash>
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div class="mt-4 px-6 pb-6"
              v-if="documentControlRecordsPaginatorInfo && documentControlRecordsPaginatorInfo.total > 0">
              <pagination :paginatorInfo="documentControlRecordsPaginatorInfo" :setPage="setPage"></pagination>
            </div>
          </div>
        </div>

        <!-- Create Modal -->
        <transition name="fade" mode="out-in">
          <base-full-modal v-if="createModal" title="Create Document Control" @close="closeCreateModal">
            <div class="p-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Document <span class="text-red-500">*</span>
                  </label>
                  <select v-model="createForm.document_direction"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg">
                    <option value="">Select</option>
                    <option value="IN">IN</option>
                    <option value="OUT">OUT</option>
                  </select>
                  <p v-if="formErrors.document_direction" class="mt-1 text-sm text-red-600">{{
                    formErrors.document_direction }}</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Reference Log Prefix <span class="text-red-500">*</span>
                  </label>
                  <select v-model="createForm.reference_prefix" @change="fetchReferencePreview"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg">
                    <option value="">Select</option>
                    <option value="CoE">CoE</option>
                    <option value="IPD">IPD</option>
                    <option value="PSC">PSC</option>
                  </select>
                  <p v-if="formErrors.reference_prefix" class="mt-1 text-sm text-red-600">{{ formErrors.reference_prefix
                  }}</p>
                </div>

                <div class="md:col-span-2"
                  v-if="createForm.reference_prefix && referencePreview && referencePreview.reference_log">
                  <div class="bg-gray-50 rounded-lg border p-4">
                    <div class="text-sm font-medium text-gray-900 mb-1">Reference Log Preview</div>
                    <div class="text-sm text-gray-700 break-words">{{ referencePreview.reference_log }}</div>
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Document Type <span class="text-red-500">*</span>
                  </label>
                  <select v-model="createForm.document_type_selection" @change="onDocumentTypeChange('create')"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg">
                    <option value="">Select</option>
                    <option v-for="dt in documentControlTypes" :key="dt.id" :value="dt.name">{{ dt.name }}</option>
                    <option value="Other">Other</option>
                  </select>
                  <p v-if="formErrors.document_type_selection" class="mt-1 text-sm text-red-600">{{
                    formErrors.document_type_selection }}</p>
                </div>

                <div v-if="createForm.document_type_selection === 'Other'">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    New Document Type <span class="text-red-500">*</span>
                  </label>
                  <input v-model="createForm.other_document_type_name" type="text"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Enter new document type name" />
                  <p v-if="formErrors.other_document_type_name" class="mt-1 text-sm text-red-600">{{
                    formErrors.other_document_type_name }}</p>
                </div>

                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Sent To Party / Person <span class="text-red-500">*</span>
                  </label>
                  <select v-model="createForm.sent_to_user_id"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg">
                    <option value="">Select</option>
                    <option v-for="u in documentControlUsers" :key="u.id" :value="u.id">{{ u.name }}</option>
                  </select>
                  <p v-if="formErrors.sent_to_user_id" class="mt-1 text-sm text-red-600">{{ formErrors.sent_to_user_id
                  }}</p>
                </div>

                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea v-model="createForm.description" rows="3"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg resize-none"
                    placeholder="Optional description"></textarea>
                </div>

                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    File Upload (Optional)
                  </label>

                  <input type="file" :accept="documentControlAccept" @change="onCreateFilesSelected"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white" />

                  <p v-if="formErrors.attachments" class="mt-1 text-sm text-red-600">{{ formErrors.attachments }}</p>

                  <div v-if="createFiles.length > 0" class="mt-4 bg-gray-50 border rounded-lg p-4">
                    <div class="text-sm font-medium text-gray-900 mb-2">Selected file</div>
                    <div v-for="(f, i) in createFiles" :key="f.name + i"
                      class="flex items-center justify-between gap-3 py-2 border-b last:border-b-0">
                      <div class="min-w-0">
                        <div class="text-sm text-gray-800 truncate">{{ f.name }}</div>
                        <div class="text-xs text-gray-500">{{ formatFileSize(f.size) }}</div>
                      </div>
                      <button type="button" class="text-red-600 hover:text-red-700 text-sm"
                        @click="removeCreateFile(i)">
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                <base-button @click="closeCreateModal" class="bg-gray-300 hover:bg-gray-400 text-gray-700">
                  Cancel
                </base-button>
                <base-button @click="submitCreate" :disabled="!canSubmitCreate || isSubmitting"
                  class="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed">
                  <div class="h-full flex justify-center items-center" v-if="isSubmitting">
                    <base-page-spinner type="spin" class-list="h-6"></base-page-spinner>
                  </div>
                  <div v-else>Create</div>
                </base-button>
              </div>
            </div>
          </base-full-modal>
        </transition>

        <!-- Edit Modal -->
        <transition name="fade" mode="out-in">
          <base-full-modal v-if="editModal"
            :title="`Edit Document Control - ${selectedItem && selectedItem.reference_log ? selectedItem.reference_log : ''}`"
            @close="closeEditModal">
            <div class="p-6">
              <div v-if="selectedItem" class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Document <span class="text-red-500">*</span>
                  </label>
                  <select v-model="editForm.document_direction"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg">
                    <option value="IN">IN</option>
                    <option value="OUT">OUT</option>
                  </select>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Reference Log <span class="text-red-500">*</span>
                  </label>
                  <input type="text" :value="selectedItem.reference_log" disabled
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100" />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Document Type <span class="text-red-500">*</span>
                  </label>
                  <select v-model="editForm.document_type_selection" @change="onDocumentTypeChange('edit')"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg">
                    <option v-for="dt in documentControlTypes" :key="dt.id" :value="dt.name">{{ dt.name }}</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div v-if="editForm.document_type_selection === 'Other'">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    New Document Type <span class="text-red-500">*</span>
                  </label>
                  <input v-model="editForm.other_document_type_name" type="text"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Enter new document type name" />
                </div>

                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Sent To Party / Person <span class="text-red-500">*</span>
                  </label>
                  <select v-model="editForm.sent_to_user_id" class="w-full px-4 py-2 border border-gray-300 rounded-lg">
                    <option v-for="u in documentControlUsers" :key="u.id" :value="u.id">{{ u.name }}</option>
                  </select>
                </div>

                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea v-model="editForm.description" rows="3"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg resize-none"
                    placeholder="Optional description"></textarea>
                </div>

                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Add More Files (Optional)
                  </label>
                  <input type="file" multiple :accept="documentControlAccept" @change="onEditFilesSelected"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white" />

                  <div v-if="editFiles.length > 0" class="mt-4 bg-gray-50 border rounded-lg p-4">
                    <div class="text-sm font-medium text-gray-900 mb-2">New files</div>
                    <div v-for="(f, i) in editFiles" :key="f.name + i"
                      class="flex items-center justify-between gap-3 py-2 border-b last:border-b-0">
                      <div class="min-w-0">
                        <div class="text-sm text-gray-800 truncate">{{ f.name }}</div>
                        <div class="text-xs text-gray-500">{{ formatFileSize(f.size) }}</div>
                      </div>
                      <button type="button" class="text-red-600 hover:text-red-700 text-sm" @click="removeEditFile(i)">
                        Remove
                      </button>
                    </div>
                  </div>
                </div>

                <div class="md:col-span-2" v-if="selectedItem.attachments && selectedItem.attachments.length > 0">
                  <div class="col-span-full bg-gray-50 border rounded-lg p-4">
                    <div class="text-sm font-medium text-gray-900 mb-2">Existing files</div>
                    <div class="flex flex-col gap-2">
                      <div v-for="att in selectedItem.attachments" :key="att.id"
                        class="flex items-center justify-between gap-3 py-2 border-b last:border-b-0">
                        <button type="button" class="text-blue-700 hover:underline text-sm text-left"
                          @click.stop="downloadAttachment(att.id)">
                          {{ att.original_filename || att.file_name }}
                        </button>
                        <span class="text-xs text-gray-500" v-if="att.file_size">{{ formatFileSize(att.file_size)
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                <base-button @click="closeEditModal" class="bg-gray-300 hover:bg-gray-400 text-gray-700">
                  Cancel
                </base-button>
                <base-button @click="submitEdit" :disabled="!canSubmitEdit || isSubmitting"
                  class="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed">
                  <div class="h-full flex justify-center items-center" v-if="isSubmitting">
                    <base-page-spinner type="spin" class-list="h-6"></base-page-spinner>
                  </div>
                  <div v-else>Update</div>
                </base-button>
              </div>
            </div>
          </base-full-modal>
        </transition>

        <!-- Attachment preview (base-modal) -->
        <base-modal v-if="showFilePreviewModal" :title="previewModalTitle" @close="closeFilePreviewModal" size="large">
          <div class="preview-container flex flex-col h-full">
            <div v-if="previewLoading" class="flex justify-center items-center py-16">
              <base-page-spinner type="spin" class-list="h-12"></base-page-spinner>
            </div>
            <div v-else-if="previewError" class="text-center py-8">
              <font-awesome-icon icon="exclamation-triangle" class="text-red-500 text-4xl mb-4" />
              <p class="text-red-600 mb-4">{{ previewError }}</p>
              <base-button v-if="previewAttachment" type="button" @click="downloadAttachment(previewAttachment.id)"
                class="bg-blue-600 hover:bg-blue-700 text-white">
                <font-awesome-icon icon="download" class="mr-2" />
                Download
              </base-button>
            </div>
            <div v-else class="preview-content flex-1 overflow-y-auto">
              <div v-if="previewAttachment && isPdfFile(previewAttachment.file_type)" class="pdf-preview">
                <iframe :src="previewUrl" class="w-full h-96 border border-gray-300 rounded-lg"
                  frameborder="0"></iframe>
              </div>
              <div v-else-if="previewAttachment && isImageFile(previewAttachment.file_type)"
                class="image-preview text-center">
                <img :src="previewUrl" alt="" class="max-w-full max-h-96 mx-auto rounded-lg shadow-lg" />
              </div>
              <div v-else-if="previewAttachment && isVideoFile(previewAttachment.file_type)"
                class="video-preview text-center">
                <video controls class="w-full max-h-96 mx-auto rounded-lg" :src="previewUrl"></video>
              </div>
              <div v-else-if="previewAttachment && isTextFile(previewAttachment.file_type)" class="text-preview">
                <iframe :src="previewUrl" class="w-full h-96 border border-gray-300 rounded-lg"
                  frameborder="0"></iframe>
              </div>
              <div
                v-else-if="previewAttachment && isDocxFile(previewAttachment.file_type) && useMammothFallback && previewHtml"
                class="docx-preview p-4">
                <div v-html="previewHtml" class="docx-content prose max-w-none"></div>
              </div>
              <div v-else-if="previewAttachment && isOfficeDocument(previewAttachment.file_type)"
                class="office-fallback text-center py-8">
                <font-awesome-icon :icon="getFileIcon(previewAttachment.file_type)"
                  :class="getFileIconColor(previewAttachment.file_type)" class="text-6xl mb-4" />
                <p class="text-gray-600 mb-4">
                  Preview is not available for this file type in the browser. Download to open it.
                </p>
              </div>
              <div v-else class="fallback-preview text-center py-8">
                <font-awesome-icon v-if="previewAttachment" :icon="getFileIcon(previewAttachment.file_type)"
                  :class="getFileIconColor(previewAttachment.file_type)" class="text-6xl mb-4" />
                <p class="text-gray-600 mb-4">
                  Preview not available for this file type. Use download to view.
                </p>
              </div>
            </div>
            <div v-if="!previewLoading && !previewError && previewAttachment"
              class="flex justify-end pt-4 border-t border-gray-200 bg-white">
              <base-button type="button" @click="downloadAttachment(previewAttachment.id)"
                class="bg-blue-600 hover:bg-blue-700 text-white">
                <font-awesome-icon icon="download" class="mr-2" />
                Download
              </base-button>
            </div>
          </div>
        </base-modal>

        <!-- Upload attachment (base-modal) -->
        <base-modal v-if="showUploadAttachmentModal" title="Upload attachment" @close="closeUploadAttachmentModal">
          <div class="p-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">File</label>
            <input type="file" ref="uploadAttachmentInput" :accept="documentControlAccept"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white"
              @change="onUploadAttachmentFileSelected" />
            <p v-if="uploadAttachmentError" class="mt-2 text-sm text-red-600">{{ uploadAttachmentError }}</p>
            <div class="flex justify-end gap-2 mt-6">
              <base-button type="button" @click="closeUploadAttachmentModal"
                class="bg-gray-300 hover:bg-gray-400 text-gray-700">
                Cancel
              </base-button>
              <base-button type="button" @click="submitUploadAttachment"
                :disabled="uploadAttachmentSubmitting || !uploadAttachmentFile"
                class="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed">
                <span v-if="uploadAttachmentSubmitting">Uploading…</span>
                <span v-else>Upload</span>
              </base-button>
            </div>
          </div>
        </base-modal>

        <!-- Export Loading Overlay -->
        <div v-if="isExportExcel"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div class="flex items-center gap-4">
              <base-page-spinner type="spin" class-list="h-10"></base-page-spinner>
              <div class="min-w-0">
                <div class="text-gray-900 font-semibold">Preparing export…</div>
                <div class="text-sm text-gray-500">This may take a moment for large datasets.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import gql from "graphql-tag";
import mammoth from "mammoth";
import Pagination from "../../components/shared/pagination.vue";
import IconPlus from "../../components/icons/IconPlus.vue";
import IconPen from "../../components/icons/IconPen.vue";
import IconTrash from "../../components/icons/IconTrash.vue";
import BaseModal from "../../components/ui/BaseModal.vue";
import xlsx from "xlsx/dist/xlsx.full.min.js";

export default {
  name: "document-control",
  components: {
    Pagination,
    IconPlus,
    IconPen,
    IconTrash,
    BaseModal,
  },
  data() {
    return {
      isLoading: false,
      isExportExcel: false,
      isSubmitting: false,

      documentControlRecords: [],
      documentControlRecordsPaginatorInfo: null,
      first: 10,
      page: 1,

      documentControlTypes: [],
      documentControlUsers: [],

      // Create modal state
      createModal: false,
      createForm: {
        document_direction: "",
        reference_prefix: "",
        document_type_selection: "",
        other_document_type_name: "",
        sent_to_user_id: "",
        description: "",
        attachments: [],
      },
      createFiles: [],
      referencePreview: null,

      // Edit modal state
      editModal: false,
      selectedItem: null,
      editForm: {
        document_direction: "",
        document_type_selection: "",
        other_document_type_name: "",
        sent_to_user_id: "",
        description: "",
        attachments: [],
      },
      editFiles: [],

      formErrors: {},

      documentControlAccept:
        ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.heic,.bmp,.tiff,.mp4,.mov,.avi,.mkv,.wmv,.zip,.rar",

      generatedReport: [],
      isApiCallRecurisive: false,

      showFilePreviewModal: false,
      previewAttachment: null,
      previewUrl: null,
      previewHtml: null,
      previewLoading: false,
      previewError: null,
      useMammothFallback: false,

      showUploadAttachmentModal: false,
      uploadAttachmentRecordId: null,
      uploadAttachmentFile: null,
      uploadAttachmentSubmitting: false,
      uploadAttachmentError: null,
    };
  },
  computed: {
    previewModalTitle() {
      if (this.previewAttachment && (this.previewAttachment.original_filename || this.previewAttachment.file_name)) {
        return (
          "Preview: " +
          (this.previewAttachment.original_filename || this.previewAttachment.file_name)
        );
      }
      return "Attachment preview";
    },
    canSubmitCreate() {
      return (
        this.createForm.document_direction &&
        this.createForm.reference_prefix &&
        this.createForm.document_type_selection &&
        (this.createForm.document_type_selection !== "Other" ||
          (this.createForm.other_document_type_name && this.createForm.other_document_type_name.trim())) &&
        this.createForm.sent_to_user_id
      );
    },
    canSubmitEdit() {
      return (
        this.editForm.document_direction &&
        this.editForm.document_type_selection &&
        (this.editForm.document_type_selection !== "Other" ||
          (this.editForm.other_document_type_name && this.editForm.other_document_type_name.trim())) &&
        this.editForm.sent_to_user_id
      );
    },
  },
  watch: {
    createModal(val) {
      if (!val) {
        this.referencePreview = null;
      }
    },
  },
  mounted() {
    const canIndex = this.hasPermission("document-control-index");
    const canCreate = this.hasPermission("document-control-create");
    const canUpdate = this.hasPermission("document-control-update");

    if (canIndex || canCreate || canUpdate) {
      this.fetchDropdowns();
    }

    if (canIndex) {
      this.fetchDocumentControlRecords();
    }
  },
  methods: {
    hasPermission(crudName) {
      const permissions = this.$store.getters.permissions || "";
      const permissionsArray = permissions.split(",");
      return permissionsArray.includes(crudName);
    },
    rowNumber(index) {
      if (
        this.documentControlRecordsPaginatorInfo &&
        this.documentControlRecordsPaginatorInfo.firstItem
      ) {
        return this.documentControlRecordsPaginatorInfo.firstItem + index;
      }
      return index + 1;
    },
    setPage(pageNo) {
      this.page = pageNo;
      this.fetchDocumentControlRecords();
    },
    formatDateDDMMYYYY(value) {
      if (!value) {
        return "—";
      }
      var d = new Date(value);
      if (isNaN(d.getTime())) {
        return String(value);
      }
      var dd = String(d.getDate()).padStart(2, "0");
      var mm = String(d.getMonth() + 1).padStart(2, "0");
      var yyyy = String(d.getFullYear());
      return dd + "-" + mm + "-" + yyyy;
    },

    getFileIcon(fileType) {
      var iconMap = {
        "application/pdf": "file-pdf",
        "application/msword": "file-word",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "file-word",
        "application/vnd.ms-excel": "file-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "file-excel",
        "application/vnd.ms-powerpoint": "file-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation": "file-powerpoint",
        "text/plain": "file-alt",
        "image/jpeg": "file-image",
        "image/png": "file-image",
        "image/gif": "file-image",
      };
      return iconMap[fileType] ? iconMap[fileType] : "file";
    },

    getFileIconColor(fileType) {
      var colorMap = {
        "application/pdf": "text-red-600",
        "application/msword": "text-blue-600",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "text-blue-600",
        "application/vnd.ms-excel": "text-green-600",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "text-green-600",
        "application/vnd.ms-powerpoint": "text-orange-600",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation": "text-orange-600",
        "text/plain": "text-gray-600",
        "image/jpeg": "text-purple-600",
        "image/png": "text-purple-600",
        "image/gif": "text-purple-600",
      };
      return colorMap[fileType] ? colorMap[fileType] : "text-gray-600";
    },

    isImageFile(fileType) {
      var imageTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/bmp",
        "image/webp",
        "image/svg+xml",
        "image/tiff",
        "image/tif",
      ];
      return imageTypes.indexOf(fileType) !== -1;
    },

    isPdfFile(fileType) {
      return fileType === "application/pdf";
    },

    isTextFile(fileType) {
      var textTypes = [
        "text/plain",
        "text/html",
        "text/css",
        "text/javascript",
        "text/csv",
        "application/json",
        "application/xml",
        "text/xml",
      ];
      return textTypes.indexOf(fileType) !== -1;
    },

    isVideoFile(fileType) {
      var videoTypes = [
        "video/mp4",
        "video/quicktime",
        "video/x-msvideo",
        "video/x-ms-wmv",
        "video/webm",
        "video/ogg",
      ];
      return videoTypes.indexOf(fileType) !== -1;
    },

    isOfficeDocument(fileType) {
      var officeTypes = [
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      ];
      return officeTypes.indexOf(fileType) !== -1;
    },

    isDocxFile(fileType) {
      var docxTypes = [
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      return docxTypes.indexOf(fileType) !== -1;
    },

    openAttachmentPreview(attachment) {
      this.previewAttachment = attachment;
      this.previewLoading = true;
      this.previewError = null;
      this.previewHtml = null;
      this.useMammothFallback = false;
      if (this.previewUrl && String(this.previewUrl).indexOf("blob:") === 0) {
        window.URL.revokeObjectURL(this.previewUrl);
      }
      this.previewUrl = null;
      this.showFilePreviewModal = true;
      this.loadAttachmentPreviewContent(attachment && attachment.id ? attachment.id : null);
    },

    closeFilePreviewModal() {
      if (this.previewUrl && String(this.previewUrl).indexOf("blob:") === 0) {
        window.URL.revokeObjectURL(this.previewUrl);
      }
      this.showFilePreviewModal = false;
      this.previewAttachment = null;
      this.previewUrl = null;
      this.previewHtml = null;
      this.previewError = null;
      this.useMammothFallback = false;
      this.previewLoading = false;
    },

    async loadAttachmentPreviewContent(attachmentId) {
      if (!attachmentId) {
        this.previewError = "Invalid attachment.";
        this.previewLoading = false;
        return;
      }
      try {
        var result = await this.$apollo.mutate({
          mutation: gql`
            mutation GetDocumentControlAttachmentContent($id: ID!) {
              getDocumentControlAttachmentContent(id: $id) {
                error
                error_code
                message
                data {
                  content
                  file_type
                  filename
                  attachment {
                    id
                    file_name
                    original_filename
                    file_type
                  }
                }
              }
            }
          `,
          variables: { id: attachmentId },
        });

        var data = result && result.data ? result.data : null;
        var payload = data && data.getDocumentControlAttachmentContent ? data.getDocumentControlAttachmentContent : null;
        if (!payload || payload.error || !payload.data || !payload.data.content) {
          this.previewError =
            payload && payload.message ? payload.message : "Failed to load file.";
          this.previewLoading = false;
          return;
        }

        var base64 = payload.data.content;
        var mime = payload.data.file_type ? payload.data.file_type : "application/octet-stream";

        var byteCharacters = atob(base64);
        var byteNumbers = new Array(byteCharacters.length);
        for (var i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        var byteArray = new Uint8Array(byteNumbers);
        var blob = new Blob([byteArray], { type: mime });
        var url = window.URL.createObjectURL(blob);
        this.previewUrl = url;

        var ft =
          this.previewAttachment && this.previewAttachment.file_type
            ? this.previewAttachment.file_type
            : mime;

        if (this.isDocxFile(ft)) {
          try {
            var mammothResult = await mammoth.convertToHtml({ arrayBuffer: byteArray.buffer });
            this.previewHtml = mammothResult.value;
            this.useMammothFallback = true;
          } catch (convErr) {
            console.error(convErr);
            this.previewHtml = null;
            this.useMammothFallback = false;
          }
        }

        this.previewLoading = false;
      } catch (e) {
        console.error(e);
        this.previewError = "Failed to load preview.";
        this.previewLoading = false;
      }
    },

    openUploadAttachmentModal(record) {
      this.uploadAttachmentRecordId = record && record.id ? record.id : null;
      this.uploadAttachmentFile = null;
      this.uploadAttachmentError = null;
      this.showUploadAttachmentModal = true;
      var self = this;
      this.$nextTick(function () {
        if (self.$refs.uploadAttachmentInput) {
          self.$refs.uploadAttachmentInput.value = "";
        }
      });
    },

    closeUploadAttachmentModal() {
      this.showUploadAttachmentModal = false;
      this.uploadAttachmentRecordId = null;
      this.uploadAttachmentFile = null;
      this.uploadAttachmentError = null;
      this.uploadAttachmentSubmitting = false;
      if (this.$refs.uploadAttachmentInput) {
        this.$refs.uploadAttachmentInput.value = "";
      }
    },

    onUploadAttachmentFileSelected(e) {
      var files = e.target.files;
      if (!files || files.length === 0) {
        this.uploadAttachmentFile = null;
        return;
      }
      this.uploadAttachmentFile = files[0];
      this.uploadAttachmentError = null;
    },

    async submitUploadAttachment() {
      if (!this.uploadAttachmentRecordId) {
        this.uploadAttachmentError = "Missing record.";
        return;
      }
      if (!this.uploadAttachmentFile) {
        this.uploadAttachmentError = "Please choose a file.";
        return;
      }
      var maxBytes = 50 * 1024 * 1024;
      if (this.uploadAttachmentFile.size > maxBytes) {
        this.uploadAttachmentError = "File must be <= 50MB.";
        return;
      }

      this.uploadAttachmentSubmitting = true;
      this.uploadAttachmentError = null;

      try {
        var response = await this.$apollo.mutate({
          mutation: gql`
            mutation AddDocumentControlAttachment($document_control_record_id: ID!, $file: Upload!) {
              addDocumentControlAttachment(document_control_record_id: $document_control_record_id, file: $file) {
                error
                error_code
                message
                data {
                  id
                }
              }
            }
          `,
          variables: {
            document_control_record_id: String(this.uploadAttachmentRecordId),
            file: this.uploadAttachmentFile,
          },
          context: {
            headers: {
              "content-type": "multipart/form-data",
            },
          },
        });

        var respData = response && response.data ? response.data : null;
        var payload = respData && respData.addDocumentControlAttachment ? respData.addDocumentControlAttachment : null;
        if (payload && payload.error) {
          this.uploadAttachmentError = payload.message ? payload.message : "Upload failed.";
          return;
        }

        this.$swal({
          icon: "success",
          title: "Uploaded",
          text: payload && payload.message ? payload.message : "Attachment uploaded.",
          timer: 2000,
        });
        this.closeUploadAttachmentModal();
        this.fetchDocumentControlRecords();
      } catch (err) {
        console.error(err);
        this.uploadAttachmentError = "Upload failed.";
      } finally {
        this.uploadAttachmentSubmitting = false;
      }
    },

    formatFileSize(bytes) {
      const n = Number(bytes);
      if (!n || n <= 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
      const i = Math.floor(Math.log(n) / Math.log(k));
      const value = n / Math.pow(k, i);
      return parseFloat(value.toFixed(2)) + " " + sizes[i];
    },

    async fetchDropdowns() {
      await Promise.all([this.fetchDocumentControlTypes(), this.fetchDocumentControlUsers()]);
    },

    async fetchDocumentControlTypes() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetDocumentControlTypes {
              documentControlTypes {
                error
                error_code
                message
                data {
                  id
                  name
                }
              }
            }
          `,
          fetchPolicy: "network-only",
        });

        if (data.documentControlTypes && data.documentControlTypes.error) {
          this.$swal({
            icon: "error",
            title: "Error",
            text: data.documentControlTypes.message || "Failed to fetch document types.",
          });
          return;
        }

        this.documentControlTypes = (data.documentControlTypes && data.documentControlTypes.data) || [];
      } catch (e) {
        console.error(e);
      }
    },

    async fetchDocumentControlUsers() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetDocumentControlUsers {
              documentControlUsers {
                error
                error_code
                message
                data {
                  id
                  name
                  email
                  position
                }
              }
            }
          `,
          fetchPolicy: "network-only",
        });

        if (data.documentControlUsers && data.documentControlUsers.error) {
          this.$swal({
            icon: "error",
            title: "Error",
            text: data.documentControlUsers.message || "Failed to fetch users.",
          });
          return;
        }

        this.documentControlUsers = (data.documentControlUsers && data.documentControlUsers.data) || [];
      } catch (e) {
        console.error(e);
      }
    },

    onDocumentTypeChange(mode) {
      if (mode === "create") {
        if (this.createForm.document_type_selection !== "Other") {
          this.createForm.other_document_type_name = "";
        }
      }
      if (mode === "edit") {
        if (this.editForm.document_type_selection !== "Other") {
          this.editForm.other_document_type_name = "";
        }
      }
    },

    async fetchReferencePreview() {
      this.referencePreview = null;
      if (!this.createForm.reference_prefix) {
        return;
      }

      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query NextReferenceLog($prefix: String!) {
              getNextDocumentControlReferenceLog(prefix: $prefix) {
                error
                error_code
                message
                data {
                  prefix
                  year
                  counter
                  reference_log
                }
              }
            }
          `,
          variables: { prefix: this.createForm.reference_prefix },
          fetchPolicy: "network-only",
        });

        const payload = data.getNextDocumentControlReferenceLog;
        if (payload && payload.error) {
          return;
        }

        this.referencePreview = payload ? payload.data : null;
      } catch (e) {
        console.error(e);
      }
    },

    async fetchDocumentControlRecords() {
      this.isLoading = true;
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query DocumentControls($first: Int, $page: Int) {
              documentControls(first: $first, page: $page) {
                error
                error_code
                message
                paginatorInfo {
                  count
                  currentPage
                  firstItem
                  hasMorePages
                  lastItem
                  lastPage
                  perPage
                  total
                }
                data {
                  id
                  document_direction
                  reference_log
                  description
                  created_at
                  document_type {
                    id
                    name
                  }
                  created_by_user {
                    id
                    name
                  }
                  sent_to_user {
                    id
                    name
                  }
                  attachments {
                    id
                    file_name
                    file_path
                    original_filename
                    file_size
                    file_type
                  }
                }
              }
            }
          `,
          variables: {
            first: this.first,
            page: this.page,
          },
          fetchPolicy: "network-only",
        });

        if (data.documentControls && data.documentControls.error) {
          this.documentControlRecords = [];
          this.documentControlRecordsPaginatorInfo = null;
          return;
        }

        this.documentControlRecords = (data.documentControls && data.documentControls.data) || [];
        this.documentControlRecordsPaginatorInfo = data.documentControls.paginatorInfo;
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },

    onCreateFilesSelected(e) {
      const files = e.target.files;
      if (!files || files.length === 0) {
        this.createFiles = [];
        this.createForm.attachments = [];
        this.formErrors.attachments = null;
        return;
      }

      const maxBytes = 50 * 1024 * 1024;
      const f = files[0];
      if (f && f.size > maxBytes) {
        this.formErrors.attachments = "File must be <= 50MB.";
        return;
      }

      // Single file only
      this.createFiles = [f];
      this.createForm.attachments = this.createFiles;
      this.formErrors.attachments = null;
    },

    removeCreateFile(index) {
      this.createFiles.splice(index, 1);
      this.createForm.attachments = this.createFiles;
    },

    onEditFilesSelected(e) {
      const files = e.target.files;
      if (!files || files.length === 0) {
        return;
      }

      const maxBytes = 50 * 1024 * 1024;
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        if (f.size > maxBytes) {
          this.formErrors.attachments = "Each file must be <= 50MB.";
          return;
        }
      }

      for (let i = 0; i < files.length; i++) {
        this.editFiles.push(files[i]);
      }

      this.editForm.attachments = this.editFiles;
      this.formErrors.attachments = null;
    },

    removeEditFile(index) {
      this.editFiles.splice(index, 1);
      this.editForm.attachments = this.editFiles;
    },

    closeCreateModal() {
      this.createModal = false;
      this.resetCreateForm();
    },

    resetCreateForm() {
      this.createForm = {
        document_direction: "",
        reference_prefix: "",
        document_type_selection: "",
        other_document_type_name: "",
        sent_to_user_id: "",
        description: "",
        attachments: [],
      };
      this.createFiles = [];
      this.formErrors = {};
      this.referencePreview = null;
    },

    openEdit(item) {
      this.selectedItem = item;
      this.editModal = true;

      this.editForm = {
        document_direction: item.document_direction || "",
        document_type_selection: item.document_type && item.document_type.name ? item.document_type.name : "",
        other_document_type_name: "",
        sent_to_user_id: item.sent_to_user && item.sent_to_user.id ? item.sent_to_user.id : "",
        description: item.description || "",
        attachments: [],
      };
      this.editFiles = [];
      this.formErrors = {};
    },

    closeEditModal() {
      this.editModal = false;
      this.selectedItem = null;
      this.editFiles = [];
      this.formErrors = {};
    },

    validateCreateForm() {
      const errs = {};

      if (!this.createForm.document_direction) errs.document_direction = "Document is required.";
      if (!this.createForm.reference_prefix) errs.reference_prefix = "Reference prefix is required.";
      if (!this.createForm.document_type_selection) errs.document_type_selection = "Document type is required.";

      if (this.createForm.document_type_selection === "Other" && !this.createForm.other_document_type_name.trim()) {
        errs.other_document_type_name = "New document type is required.";
      }

      if (!this.createForm.sent_to_user_id) errs.sent_to_user_id = "Sent To user is required.";

      this.formErrors = errs;
      return Object.keys(errs).length === 0;
    },

    async submitCreate() {
      if (!this.validateCreateForm()) {
        return;
      }

      this.isSubmitting = true;
      try {
        const CREATE_MUTATION = gql`
          mutation CreateDocumentControl($input: CreateDocumentControlInput!) {
            createDocumentControl(input: $input) {
              error
              error_code
              message
              data {
                id
                reference_log
              }
            }
          }
        `;

        const input = {
          document_direction: this.createForm.document_direction,
          reference_prefix: this.createForm.reference_prefix,
          document_type_selection: this.createForm.document_type_selection,
          other_document_type_name: this.createForm.other_document_type_name || null,
          sent_to_user_id: this.createForm.sent_to_user_id,
          description: this.createForm.description || null,
          attachments: (this.createFiles && this.createFiles.length > 0) ? this.createFiles : null,
        };

        const response = await this.$apollo.mutate({
          mutation: CREATE_MUTATION,
          variables: { input },
          context: {
            headers: {
              "content-type": "multipart/form-data",
            },
          },
        });

        const payload = response.data.createDocumentControl;
        if (payload && payload.error) {
          this.$swal({ icon: "error", title: "Oops...", text: payload.message });
          return;
        }

        this.$swal({ icon: "success", title: "Success", text: payload.message });
        this.closeCreateModal();
        this.fetchDocumentControlRecords();
        await this.fetchDocumentControlTypes();
      } catch (e) {
        console.error(e);
        this.$swal({ icon: "error", title: "Oops...", text: "Failed to create record." });
      } finally {
        this.isSubmitting = false;
      }
    },

    async submitEdit() {
      if (!this.canSubmitEdit) {
        return;
      }

      this.isSubmitting = true;
      try {
        const UPDATE_MUTATION = gql`
          mutation UpdateDocumentControl($input: UpdateDocumentControlInput!) {
            updateDocumentControl(input: $input) {
              error
              error_code
              message
              data {
                id
                reference_log
              }
            }
          }
        `;

        const input = {
          id: this.selectedItem.id,
          document_direction: this.editForm.document_direction,
          document_type_selection: this.editForm.document_type_selection,
          other_document_type_name: this.editForm.other_document_type_name || null,
          sent_to_user_id: this.editForm.sent_to_user_id,
          description: this.editForm.description || null,
          attachments: this.editFiles && this.editFiles.length > 0 ? this.editFiles : null,
        };

        const response = await this.$apollo.mutate({
          mutation: UPDATE_MUTATION,
          variables: { input },
          context: {
            headers: {
              "content-type": "multipart/form-data",
            },
          },
        });

        const payload = response.data.updateDocumentControl;
        if (payload && payload.error) {
          this.$swal({ icon: "error", title: "Oops...", text: payload.message });
          return;
        }

        this.$swal({ icon: "success", title: "Success", text: payload.message });
        this.closeEditModal();
        this.fetchDocumentControlRecords();
        await this.fetchDocumentControlTypes();
      } catch (e) {
        console.error(e);
        this.$swal({ icon: "error", title: "Oops...", text: "Failed to update record." });
      } finally {
        this.isSubmitting = false;
      }
    },

    async deleteItem(id) {
      if (!this.hasPermission("document-control-delete")) {
        return;
      }

      const result = await this.$swal.fire({
        icon: "warning",
        title: "Delete record?",
        text: "This action cannot be undone.",
        showCancelButton: true,
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
      });

      if (!result || !result.isConfirmed) {
        return;
      }

      try {
        const DELETE_MUTATION = gql`
          mutation DeleteDocumentControl($id: ID!) {
            deleteDocumentControl(id: $id) {
              error
              error_code
              message
            }
          }
        `;

        const response = await this.$apollo.mutate({
          mutation: DELETE_MUTATION,
          variables: { id },
        });

        const payload = response.data.deleteDocumentControl;
        if (payload && payload.error) {
          this.$swal({ icon: "error", title: "Oops...", text: payload.message });
          return;
        }

        this.$swal({ icon: "success", title: "Deleted", text: payload.message });
        this.fetchDocumentControlRecords();
      } catch (e) {
        console.error(e);
        this.$swal({ icon: "error", title: "Oops...", text: "Failed to delete record." });
      }
    },

    async generateReport() {
      if (!this.hasPermission("document-control-index")) {
        return;
      }

      this.generatedReport = [];
      this.isExportExcel = true;
      this.isApiCallRecurisive = true;

      let exportPage = 1;
      const exportLimit = 200;

      try {
        while (this.isApiCallRecurisive) {
          const responseData = await this.fetchForExporting(exportPage, exportLimit);

          if (responseData && responseData.error) {
            this.$swal({ icon: "error", title: "Oops...", text: responseData.message || "Export failed." });
            break;
          }

          const rows = responseData && responseData.data ? responseData.data : [];
          if (rows.length > 0) {
            this.generatedReport.push(
              ...rows.map((obj) => {
                const fileNames = obj.attachments && obj.attachments.length > 0
                  ? obj.attachments.map((a) => a.file_name).join(", ")
                  : "";
                return {
                  "Document": obj.document_direction,
                  "Reference Log": obj.reference_log,
                  "Document Type": obj.document_type && obj.document_type.name ? obj.document_type.name : "",
                  "Sent To": obj.sent_to_user && obj.sent_to_user.name ? obj.sent_to_user.name : "",
                  "Description": obj.description || "",
                  "Files": fileNames,
                  "Created At": obj.created_at || "",
                };
              })
            );
            exportPage++;
          } else {
            this.isApiCallRecurisive = false;
          }
        }

        this.exportToExcel("Document_Control");
      } catch (e) {
        console.error(e);
      } finally {
        this.isExportExcel = false;
        this.isApiCallRecurisive = false;
      }
    },

    async fetchForExporting(pageNo = 1, limitSize = 50) {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query ExportDocumentControls($page: Int, $limit: Int) {
              exportDocumentControls(page: $page, limit: $limit) {
                error
                error_code
                message
                data {
                  id
                  document_direction
                  reference_log
                  description
                  created_at
                  document_type {
                    name
                  }
                  sent_to_user {
                    name
                  }
                  attachments {
                    file_name
                    original_filename
                  }
                }
              }
            }
          `,
          variables: { page: pageNo, limit: limitSize },
          fetchPolicy: "network-only",
        });

        return data.exportDocumentControls;
      } catch (e) {
        console.error(e);
        return { error: true, message: e.message };
      }
    },

    exportToExcel(reportName) {
      const XLSX = xlsx;

      const worksheet = XLSX.utils.json_to_sheet(this.generatedReport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      XLSX.writeFile(workbook, `${reportName}.xlsx`);

      this.generatedReport = [];
      this.isExportExcel = false;
    },

    async downloadAttachment(attachmentId) {
      if (!attachmentId) {
        return;
      }
      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation GetDocumentControlAttachmentContent($id: ID!) {
              getDocumentControlAttachmentContent(id: $id) {
                error
                error_code
                message
                data {
                  content
                  file_type
                  filename
                  attachment {
                    id
                    file_name
                    original_filename
                    file_type
                  }
                }
              }
            }
          `,
          variables: { id: attachmentId },
        });

        const payload = data.getDocumentControlAttachmentContent;
        if (!payload || payload.error || !payload.data || !payload.data.content) {
          this.$swal({
            icon: "error",
            title: "Oops...",
            text: (payload && payload.message) ? payload.message : "Failed to download file.",
          });
          return;
        }

        const base64 = payload.data.content;
        const mime = payload.data.file_type || "application/octet-stream";
        const filename =
          payload.data.filename ||
          (payload.data.attachment && (payload.data.attachment.original_filename || payload.data.attachment.file_name)) ||
          "download";

        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mime });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        console.error(e);
        this.$swal({ icon: "error", title: "Oops...", text: "Failed to download file." });
      }
    },
  },
};
</script>

<style scoped>
.dc-export-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  border-radius: 0.75rem;
  border: 1px solid rgba(16, 185, 129, 0.25);
  background: linear-gradient(to bottom, #ffffff, rgba(16, 185, 129, 0.08));
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #052e1f;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;
}

.dc-export-btn:hover {
  border-color: rgba(16, 185, 129, 0.45);
  background: linear-gradient(to bottom, rgba(16, 185, 129, 0.08), rgba(16, 185, 129, 0.14));
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
}

.dc-export-btn:disabled {
  pointer-events: none;
  opacity: 0.6;
}

.dc-export-btn__icon {
  font-size: 1.05rem;
  color: #059669;
  filter: drop-shadow(0 1px 0 rgba(0, 0, 0, 0.06));
}

.dc-add-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  border-radius: 0.75rem;
  border: 1px solid rgba(99, 102, 241, 0.35);
  background: linear-gradient(to bottom, #6d28d9, #4f46e5);
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease, transform 150ms ease;
}

.dc-add-btn:hover {
  border-color: rgba(99, 102, 241, 0.6);
  background: linear-gradient(to bottom, #5b21b6, #4338ca);
  box-shadow: 0 6px 14px rgba(67, 56, 202, 0.22);
  transform: translateY(-1px);
}

.dc-add-btn:active {
  transform: translateY(0);
}

.dc-add-btn:disabled {
  pointer-events: none;
  opacity: 0.6;
}

.dc-add-btn__icon {
  width: 1rem;
  height: 1rem;
  display: inline-block;
}

.dc-file-icon-btn,
.dc-file-upload-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #4b5563;
  cursor: pointer;
}

.dc-file-icon-btn:hover {
  background: #eef2ff;
  color: #4338ca;
}

.dc-file-upload-btn:hover {
  background: #ecfdf5;
  color: #059669;
}
</style>
